import {
    Link,
    Container,
    Header,
    SpaceBetween,
    Box,
    Button
} from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// @ts-ignore
import { useHistory, useLocation } from 'react-router-dom';
import { AgentHawkLabel } from '../AgentHawkLabel';
import { useAgentHawkAvailability } from '../hooks/useAgentHawkAvailability';
import { AGENT_HAWK_INTRO_POINTS, AGENT_HAWK_INTRO_SEEN_KEY, getExplorerLinks } from './Links'

const HyperLink = ({text, link}) => {
    return (
        <Link href={link}>
            {text}
        </Link>
    );
};

/** localStorage can throw in hardened browser modes; never break the page for it. */
const hasSeenIntro = (): boolean => {
    try {
        return localStorage.getItem(AGENT_HAWK_INTRO_SEEN_KEY) === 'true';
    } catch {
        return true;
    }
};

const markIntroSeen = (): void => {
    try {
        localStorage.setItem(AGENT_HAWK_INTRO_SEEN_KEY, 'true');
    } catch {
        // Nothing to do: the popover simply shows again next time.
    }
};

/**
 * Agent Hawk entry point for the Explorer Links panel, with an intro popover
 * that opens automatically on the user's first page load and is then suppressed
 * via localStorage. Renders only where Agent Hawk is available for the current
 * stage/region.
 */
const AgentHawkExplorerLink = () => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { available } = useAgentHawkAvailability();
    const [introOpen, setIntroOpen] = useState(() => !hasSeenIntro());
    const wrapperRef = useRef<HTMLDivElement>(null);

    const dismissIntro = useCallback(() => {
        setIntroOpen(false);
        markIntroSeen();
    }, []);

    const openAgentHawk = useCallback(() => {
        dismissIntro();
        // Swap the current tab segment for the Agent Hawk tab.
        history.push({ pathname: `${pathname.replace(/\/$/, '').split('/').slice(0, -1).join('/')}/agenthawk` });
    }, [dismissIntro, history, pathname]);

    useEffect(() => {
        if (!introOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') dismissIntro();
        };
        const onPointerDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) dismissIntro();
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [introOpen, dismissIntro]);

    // Region allowlist: render no entry point (and no popover) where Agent Hawk
    // is unavailable, so the one-time intro is not spent on an unsupported region.
    if (!available) return null;

    return (
        <div className="agent-hawk-link-wrapper" ref={wrapperRef}>
            <button
                type="button"
                className="agent-hawk-link-trigger"
                onClick={openAgentHawk}
                aria-label="Open Agent Hawk"
            >
                <AgentHawkLabel iconSize={20} />
            </button>

            {introOpen && (
                <div className="agent-hawk-intro" role="dialog" aria-label="What you can do with Agent Hawk">
                    <div className="agent-hawk-intro__arrow" />
                    <div className="agent-hawk-intro__header">
                        <span className="agent-hawk-intro__title">Try Agent Hawk - AI Ops Assistant</span>
                        <Button
                            variant="icon"
                            iconName="close"
                            ariaLabel="Dismiss Agent Hawk introduction"
                            onClick={dismissIntro}
                        />
                    </div>
                    <Box variant="p" fontSize="body-s" color="text-body-secondary">
                        Faster RCAs for cluster-level issues — describe what you are seeing and let it
                        find the likely cause, instead of digging through tabs.
                    </Box>
                    <ul className="agent-hawk-intro__list">
                        {AGENT_HAWK_INTRO_POINTS.map(point => (
                            <li key={point}>{point}</li>
                        ))}
                    </ul>
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="primary" onClick={openAgentHawk}>Open Agent Hawk</Button>
                        <Button variant="link" onClick={dismissIntro}>Maybe later</Button>
                    </SpaceBetween>
                </div>
            )}
        </div>
    );
};

const ExplorerLinks = () => {
    return(
        <Container
            header={
                <Header
                    variant="h2"
                    description="Use the below URLs to list available commands and their usage details."
                    actions={<AgentHawkExplorerLink />}
                >
                    Explorer Links
                </Header>
            }
        >
            <SpaceBetween size="l">
            {getExplorerLinks().map(({ text, link }) => (
                <HyperLink key={link} text={text} link={link} />
            ))}
            </SpaceBetween>
        </Container>
    );
};

export { ExplorerLinks };
